import React, { useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  Heading,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from '@eluve/components';
import {
  useAppointmentId,
  useAppointmentLocationId,
  useAppointmentPatient,
  useExternalAppointmentId,
  useIsAppointmentReadonly,
  useSignNoteOnEluve,
  useSummary,
} from '@eluve/frontend-appointment-hooks';
import { useCaptureEvent } from '@eluve/posthog-react';
import { useTenantIdFromParams } from '@eluve/session-helpers';

export const SignNoteOnEluveButton: React.FC = () => {
  const signNoteOnEluve = useSignNoteOnEluve();
  const { isEmpty } = useSummary();

  const [confirmationDialogOpen, setConfirmationDialogOpen] =
    useState<boolean>(false);

  const tenantId = useTenantIdFromParams() ?? '';

  const appointmentId = useAppointmentId();
  const appointmentExternalId = useExternalAppointmentId();
  const isAppointmentReadonly = useIsAppointmentReadonly();
  const appointmentLocationId = useAppointmentLocationId();
  const toast = useToast();

  const captureEvent = useCaptureEvent({
    tenantId,
    appointmentId,
    externalAppointmentId: appointmentExternalId,
  });

  const patient = useAppointmentPatient();
  const patientId = patient?.id;

  const showSuccessToast = (text: string) => {
    toast.success('Note Signed Successfully', { description: text });
  };

  const onButtonClick = async () => {
    // Open the dialog with the confirmation content
    setConfirmationDialogOpen(true);
  };

  const signNoteOnEluveOnly = async (): Promise<void> => {
    setConfirmationDialogOpen(false);
    const isSubmitted = await signNoteOnEluve();

    if (isSubmitted) {
      showSuccessToast(`Your note has been signed.`);
      captureEvent('signed_note_on_eluve_only');
    } else {
      captureEvent('failed_to_sign_note_on_eluve_only');
      toast.error('Failed to sign note.');
    }
  };

  const signNoteDisabledReasonText = (() => {
    if (isAppointmentReadonly) {
      return 'Only the appointment owner may sign this note.';
    }
    if (isEmpty) {
      return `The chart hasn't been modified. Please fill out the chart before signing the note.`;
    }
    if (!appointmentLocationId) {
      return 'Select a location before signing the note.';
    }
    if (!patientId) {
      return 'Select a patient before signing the note.';
    }
  })();

  const signNoteDisabled = Boolean(signNoteDisabledReasonText);

  return (
    <div className="w-full">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button disabled={signNoteDisabled} onClick={onButtonClick} fullWidth>
            Sign Note
          </Button>
        </TooltipTrigger>
        {signNoteDisabled && (
          <TooltipContent>{signNoteDisabledReasonText}</TooltipContent>
        )}
      </Tooltip>

      <Dialog
        open={confirmationDialogOpen}
        onOpenChange={setConfirmationDialogOpen}
      >
        <DialogContent
          size="lg"
          className="flex flex-col items-center justify-center gap-5 p-0"
        >
          <Stack className="p-10" align="center" gap={5}>
            <Heading size="3xl">Are you sure?</Heading>

            <Text color="tertiary" className="text-center">
              You will no longer be able to edit this note in Eluve.
            </Text>
            <Stack
              direction="horizontal"
              align="center"
              justify="center"
              gap={3}
            >
              <Button
                className="border border-border-secondary bg-white text-text-tertiary hover:bg-white"
                onClick={() => setConfirmationDialogOpen(false)}
              >
                Cancel
              </Button>

              <Button onClick={() => signNoteOnEluveOnly()}>
                Yes, Sign Note
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
    </div>
  );
};
